import { useEffect, useState } from "react";
import client from "../api/client";
import { CZ_MONTHS, formatDateFull } from "../utils/format";
import { ChevronLeft, ChevronRight, X, Mail, Phone, Navigation } from "lucide-react";
import { mapsUrl } from "../utils/format";

const WD = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

export default function Calendar() {
  const [current, setCurrent] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [workshops, setWorkshops] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    client.get("/workshops", { params: { filter: "all" } }).then((res) => setWorkshops(res.data));
  }, []);

  const byDate = {};
  workshops.forEach((w) => { (byDate[w.date] = byDate[w.date] || []).push(w); });

  const conflictDays = {};
  Object.entries(byDate).forEach(([d, evs]) => {
    const seen = {};
    evs.forEach((w) => (w.lector_ids || []).forEach((id, idx) => {
      if (!id) return;
      seen[id] = seen[id] || { name: (w.lector_names || [])[idx] || "lektor", count: 0 };
      seen[id].count += 1;
    }));
    const names = Object.values(seen).filter((x) => x.count > 1).map((x) => x.name);
    if (names.length) conflictDays[d] = names;
  });
  const conflictList = Object.entries(conflictDays);

  const first = new Date(current.y, current.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(current.y, current.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prev = () => setCurrent((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCurrent((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const dateKey = (d) => `${current.y}-${String(current.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <>
      <div className="h-16 border-b border-border px-8 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-10">
        <h1 className="font-heading text-xl font-semibold tracking-tight">Kalendář</h1>
        <div className="flex items-center gap-3">
          <button onClick={prev} data-testid="cal-prev" className="p-2 rounded-md hover:bg-secondary"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-medium w-40 text-center">{CZ_MONTHS[current.m]} {current.y}</span>
          <button onClick={next} data-testid="cal-next" className="p-2 rounded-md hover:bg-secondary"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="p-8">
        {conflictList.length > 0 && (
          <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-700" data-testid="conflict-banner">
            <strong>⚠ Konflikty lektorů:</strong>{" "}
            {conflictList.map(([d, names]) => `${d.split("-").reverse().join(".")} – ${names.join(", ")}`).join(" · ")}
          </div>
        )}
        <div className="grid grid-cols-7 gap-px bg-border border border-border rounded-lg overflow-hidden" data-testid="calendar-grid">
          {WD.map((d) => <div key={d} className="bg-muted/50 px-2 py-2 text-xs uppercase tracking-wide text-muted-foreground font-medium text-center">{d}</div>)}
          {cells.map((d, i) => {
            const key = d ? dateKey(d) : null;
            const events = key ? byDate[key] || [] : [];
            const conflict = key ? conflictDays[key] : null;
            return (
              <div key={i} className="bg-background min-h-[100px] p-1.5 align-top">
                {d && (
                  <>
                    <div className="flex items-center justify-between px-1 mb-1">
                      <span className="text-xs text-muted-foreground">{d}</span>
                      {conflict && <span title={`Konflikt lektorů: ${conflict.join(", ")}`} className="text-[9px] text-red-600 font-semibold" data-testid={`cal-conflict-${key}`}>⚠</span>}
                    </div>
                    <div className="space-y-1">
                      {events.map((w) => (
                        <button
                          key={w.id}
                          data-testid={`cal-event-${w.id}`}
                          onClick={() => setSelected(w)}
                          className="w-full text-left text-[11px] px-1.5 py-1 rounded truncate text-white"
                          style={{ background: (w.lector_colors && w.lector_colors[0]) || w.status_color || "#2563eb" }}
                          title={w.school_name}
                        >
                          {w.time_from} {w.school_name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={() => setSelected(null)}>
          <div className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md" onMouseDown={(e) => e.stopPropagation()} data-testid="cal-event-modal">
            <div className="flex items-center justify-between px-5 h-14 border-b border-border">
              <h3 className="font-heading font-semibold">{selected.school_name}</h3>
              <button onClick={() => setSelected(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <p><span className="text-muted-foreground">Datum: </span>{formatDateFull(selected.date)} · {selected.time_from}–{selected.time_to}</p>
              <p><span className="text-muted-foreground">Program: </span>{selected.program_name}</p>
              <p><span className="text-muted-foreground">Adresa: </span>{selected.address}</p>
              <p><span className="text-muted-foreground">Kontakt: </span>{selected.contact_person}</p>
              <p><span className="text-muted-foreground">Lektoři: </span>{(selected.lector_names || []).join(", ")}</p>
              {selected.note && <p><span className="text-muted-foreground">Poznámka: </span>{selected.note}</p>}
              <div className="flex gap-2 pt-3">
                {selected.school_phone && <a href={`tel:${selected.school_phone}`} className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary"><Phone className="h-4 w-4" /> Zavolat</a>}
                {selected.school_email && <a href={`mailto:${selected.school_email}`} className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary"><Mail className="h-4 w-4" /> E-mail</a>}
                <a href={mapsUrl(selected.address)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary"><Navigation className="h-4 w-4" /> Navigace</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
